/* eslint-disable react/no-danger */
import React from 'react'
import SectionCta from '@components/Button/button'
import ErrorBoundary from '@components/ErrorBoundary/errorBoundary'
import { convertToSlug } from 'src/utils/IdGenerator'
import { getHeadingDesc } from '@utils/schemaUtils'
import styles from './Video.module.scss'
import VideoPlayer from './VideoPlayer'
import TwoColumnVideoLayout from './TwoColumnVideoLayout'

const Video = ({ data }) => {
  const { isMobile } = data
  const videoHeader = data?.data?.attributes?.videoHeader
  const floaterCta = data?.data?.attributes?.videoCta

  const cheerio = global.cheerio

  // Extract the h2 content and headerTabName
  const h2Regex = /<h2[^>]*>(.*?)<\/h2>/
  const matches = videoHeader?.match(h2Regex)
  const h2Content = matches ? matches[1] : null
  const idContent = data?.data?.attributes?.headerTabName || h2Content

  const videoContentFull = data?.data?.attributes

  const getModifiedHtml = content => {
    if (!content) return ''
    const $ = cheerio.load(content, null, false)

    $('p').each((_index, element) => {
      const $p = $(element)
      if ($p.html().trim() === '&nbsp;') {
        $p.remove()
      }
    })

    $('a').each((_index, element) => {
      const $a = $(element)
      $a.attr('target', '_blank')
    })

    const modifiedHTML = $.html()
    return modifiedHTML
  }

  const getIso8601 = (time: { length: number; unit: 'SECONDS' | 'MINUTES' | 'HOURS' } | null): string | null => {
    if (time?.length || !time?.unit) return null

    let totalSeconds: number

    switch (time.unit) {
      case 'HOURS':
        totalSeconds = time.length * 3600
        break
      case 'MINUTES':
        totalSeconds = time.length * 60
        break
      case 'SECONDS':
        totalSeconds = time.length
        break
      default:
        return null
    }

    // Extract hours, minutes, seconds
    totalSeconds = Math.max(0, Math.floor(totalSeconds))
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    let iso = 'PT'
    if (hours > 0) iso += `${hours}H`
    if (minutes > 0) iso += `${minutes}M`
    if (seconds > 0 || iso === 'PT') iso += `${seconds}S` // include 0S if total is 0

    return iso
  } // PT4M30S

  const headings = getHeadingDesc(videoContentFull?.videoHeader)
  const duration = getIso8601(videoContentFull?.video_asset.data?.attributes?.data?.duration || null)

  const videoSchema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: headings.heading,
    description: headings.description,
    thumbnailUrl: videoContentFull?.video_asset?.data?.attributes?.data?.thumbnails?.map(
      thumbnail => thumbnail?.link || ''
    ),
    embedUrl: videoContentFull?.video_asset?.data?.attributes?.data?.streamData?.map(stream => stream?.link || ''),
    url: data.currentUrl,
    publisher: {
      '@type': 'Organization',
      name: 'ACKO',
      logo: {
        '@type': 'ImageObject',
        url: 'https://acko-brand.ackoassets.com/brand/vector-svg/gradient/horizontal-reverse.svg',
      },
    },
    potentialAction: {
      '@type': 'WatchAction',
      target: videoContentFull?.video_asset?.data?.attributes?.data?.streamData?.map(stream => stream?.link || ''),
    },
    uploadDate: videoContentFull?.video_asset?.data?.attributes?.publishedOn,
    duration,
  }

  return (
    <ErrorBoundary data={data}>
      <div className={`${styles.videoContainer} white`} id={convertToSlug(idContent)}>
        <script
          id="video-schema"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(videoSchema),
          }}
        />
        <div className={styles.videoHeader} dangerouslySetInnerHTML={{ __html: getModifiedHtml(videoHeader) }} />

        {videoContentFull?.twoColumnVideoSectionContent && videoContentFull?.twoColumnVideoLayoutType ? (
          <TwoColumnVideoLayout isMobileDevice={isMobile} twoColumnvideoData={videoContentFull} />
        ) : (
          <VideoPlayer isMobile={isMobile} videoContentFull={videoContentFull} />
        )}

        <div className={styles.videoCta}>
          <div
            className={styles.videoButtonPara}
            dangerouslySetInnerHTML={{ __html: floaterCta?.sectionCTAPara || '' }}
          />
          <SectionCta data={floaterCta} segmentEventCommonProperties={data?.segmentEventCommonProperties} />
        </div>
      </div>
    </ErrorBoundary>
  )
}

export default Video
